import React, { useMemo } from 'react';
import PropTypes from "prop-types";
import MatchScoreEditTab from "./MatchScoreEditTab";
import CoverLetterEditTab from "./CoverLetterEditTab";
import AvailabilityRateEditTab from "./AvailabilityRateEditTab";
import { useQueryClient } from '@tanstack/react-query';
import Opportunity from "../../models/Opportunity";
import DocumentsEditTab from "./DocumentsEditTab";
import ManageOppMeetings from "../../components/meetings/ManageOppMeetings";
import MessagesTab from "./MessagesTab";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Tabs } from "antd";
import { useAuth } from '../../providers/AuthProvider';
import { useOpportunity } from "../../providers/OpportunityProvider";
import TeamScopeTab from "./TeamScopeTab";
import TeamMemberPayoutEditTab from "./TeamMemberPayoutEditTab";
import FixedFeePayoutEditTab from "./FixedFeePayoutEditTab";
import FixedRetainerPayoutEditTab from './FixedRetainerPayoutEditTab';

const OpportunityProposal = (props) => {
    const {
        opportunity,
        onUpdate,
        onRequest,
        changeProps,
        isDisabled,
    } = useOpportunity();
    const isIndividualOpp = opportunity.isInterested() || opportunity.isProposingAsIndividual();
    const isTeamOpp = opportunity.isProposingWithTeam();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const { auth } = useAuth();

    const proposalTabs = useMemo(() => {
        let tabs = {
            "Team": isTeamOpp,
            "Role & Payout": isTeamOpp,
            "Cover Letter": true,
            "Payout": isIndividualOpp && opportunity.isFixedFee(),
            "Pricing": isIndividualOpp && opportunity.isFixedRetainer(),
            "Availability & Rate": isIndividualOpp && opportunity.isTimeMaterials(),
            "My Match Score": isIndividualOpp,
            "Supporting Documents": !opportunity.isInterested() && opportunity.hasSubmittedProposal(),
            "Interviews": opportunity.canMessageAndAcceptInterviews(),
            "Messages": opportunity.canMessageAndAcceptInterviews(),
        };


        return TB.reduce(tabs, (result, value, key) => {
            if (value) {
                result.push(key)
            }
            return result
        }, []);
    }, [isTeamOpp, isIndividualOpp, opportunity, isDisabled]);


    const selectedTab = useMemo(() => {

        let selected = TB.first(proposalTabs);
        if (searchParams.has('tabView')) {
            selected = TB.find(proposalTabs, tab => TB.slugify(tab) === searchParams.get('tabView'));
        }

        return selected;
    }, [proposalTabs, searchParams.get('tabView')]);

    const updateOpp = async (params) => {
        if (params) {
            let updateParams = { ...params };

            if (!opportunity.job_candidate?.talent_proposed_at) {
                updateParams.job_candidate = {
                    ...(opportunity.job_candidate || {}),
                    ...(updateParams.job_candidate || {}),
                    talent_proposed_at: new Date()
                };
                if (updateParams.job_candidate?.latest_proposal && !(updateParams.job_candidate?.latest_proposal instanceof Blob || updateParams.job_candidate?.latest_proposal instanceof File)) {
                    delete updateParams.job_candidate?.latest_proposal;
                }
            }

            let updatedOpp = await onUpdate(updateParams);

            if (updatedOpp) {
                return TB.sysAlert('talent.opportunity_proposal_save_btn_click')
            }
        }
    }

    const onSetActiveTab = (tab) => {
        let params = new URLSearchParams(searchParams);
        params.set('tabView', TB.slugify(tab))

        return setSearchParams(params)
    }

    const isSupplier = auth.isTalentSupplierResource();

    return (
        <div className="proposal-container opportunity-proposal">

            <Tabs
                activeKey={selectedTab}
                className="[&_.ant-tabs-content]:p-6 bg-white [&_.ant-tabs-tab]:text-[13px] [&_.ant-tabs-tab]:!m-0 [&_.ant-tabs-tab]:p-3 [&_.ant-tabs-tab]:px-5 [&_.ant-tabs-tab-active]:bg-white"
                onChange={tab => onSetActiveTab(tab)}
            >
                {isTeamOpp && (
                    <>
                        <Tabs.TabPane tab="Team" key="Team">
                            <TeamScopeTab onUpdate={changeProps} />
                        </Tabs.TabPane>

                        <Tabs.TabPane tab="Role & Payout" key="Role & Payout">
                            <TeamMemberPayoutEditTab onUpdate={updateOpp} />
                        </Tabs.TabPane>
                    </>
                )}

                {!isSupplier && (
                    <Tabs.TabPane tab="Cover Letter" key="Cover Letter">
                        <CoverLetterEditTab onUpdate={updateOpp} />
                    </Tabs.TabPane>
                )}

                {isIndividualOpp && !isSupplier && (
                    <>
                        {opportunity.isFixedRetainer() && (
                            <Tabs.TabPane tab="Pricing" key="Pricing">
                                <FixedRetainerPayoutEditTab onUpdate={updateOpp} />
                            </Tabs.TabPane>
                        )}

                        {opportunity.isFixedFee() && (
                            <Tabs.TabPane tab="Payout" key="Payout">
                                <FixedFeePayoutEditTab onUpdate={updateOpp} />
                            </Tabs.TabPane>
                        )}

                        {opportunity.isTimeMaterials() && (
                            <Tabs.TabPane tab="Availability & Rate" key="Availability & Rate">
                                <AvailabilityRateEditTab onUpdate={updateOpp} />
                            </Tabs.TabPane>
                        )}
                    </>
                )}

                {isIndividualOpp && (
                    <Tabs.TabPane tab="My Match Score" key="My Match Score">
                        <MatchScoreEditTab onUpdate={changeProps} />
                    </Tabs.TabPane>
                )}

                {!opportunity.isInterested() && opportunity.hasSubmittedProposal() && !isSupplier && (
                    <Tabs.TabPane tab="Supporting Documents" key="Supporting Documents">
                        <DocumentsEditTab onUpdate={updateOpp} />
                    </Tabs.TabPane>
                )}

                {opportunity.canMessageAndAcceptInterviews() && (
                    <Tabs.TabPane tab="Interviews" key="Interviews">
                        <ManageOppMeetings />
                    </Tabs.TabPane>
                )}

                {opportunity.canMessageAndAcceptInterviews() && !isSupplier && (
                    <Tabs.TabPane tab="Messages" key="Messages">
                        <div className="proposal-overview overflow-auto">
                            <div className="proposal-tabs-section">
                                <div className="proposal-tab-content p-0">
                                    <div className="proposal-messages">
                                        <MessagesTab
                                            opportunity={opportunity}
                                            candidate={opportunity.job_candidate}
                                            onFetchSuccess={() => {
                                                changeProps(
                                                    Opportunity.make({
                                                        ...opportunity,
                                                        unread_messages_count: 0
                                                    })
                                                )
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tabs.TabPane>
                )}

            </Tabs>
        </div>
    )
};

OpportunityProposal.propTypes = {
    disabled: PropTypes.bool,
    opportunity: PropTypes.any,
    onRequest: PropTypes.any
};

export default OpportunityProposal;